// Verifying content cache, keyed by CID alone (szzt-spec.md §12, §9.4).
//
// On miss the cache fetches via the injected `Fetcher`, hash-verifies the bytes against the CID (the
// same re-derivation the reference verifier and build use, via @szzt/records), and stores +
// serves ONLY on match. Bytes that fail verification are never cached or served (fail closed). The
// CID is always sourced from signed state — the caller passes locations built by resolveIndex (§9.2)
// — so a matching hash means authentic content regardless of which transport supplied the bytes.
// Cache-side verification is an optimization, not a trust dependency: serve.ts is where re-read
// verification lives, and it is this same method (§9.4).

import { recomputeBlobCid } from "@szzt/records";
import { GatewayError, RateLimitError, VerificationError } from "./types.ts";
import type { CidLocation, Fetcher } from "./types.ts";
import { NegativeCache } from "./negative-cache.ts";

/** A signed blob location: a DID + `raw` CID (+ path for diagnostics). */
export interface BlobLocation {
  did: string;
  cid: string;
  path: string;
}

/**
 * The storage seam behind {@link VerifyingCache}: a plain content-addressed byte store keyed by CID.
 * It holds ONLY bytes the cache has already re-hashed to their CID, so it carries no trust — any
 * backing (in-memory, browser Cache API, …) is safe to swap in. Async so a persistent backend (the
 * browser Cache API, {@link "./cache-storage.ts".CacheApiBlobStore}) fits without a leaky sync API.
 */
export interface BlobStore {
  /** Cached bytes for this CID, or `undefined` on a miss. */
  get(cid: string): Promise<Uint8Array | undefined>;
  /** Store verified bytes for this CID. Only ever called with bytes that hash to `cid`. */
  set(cid: string, bytes: Uint8Array): Promise<void>;
  /** Whether this CID is already stored (warm). */
  has(cid: string): Promise<boolean>;
  /** Drop this CID's bytes (a no-op on a miss). Used for capacity-driven eviction (§12) and the §13
   *  mirror unpin path; verification is unaffected — a later miss re-fetches + re-hashes as normal. */
  delete(cid: string): Promise<void>;
}

/** Default cap for {@link BoundedBlobStore}: 256 MB. Blob bytes live in external ArrayBuffer memory, so
 *  V8's heap cap never trips on them — an unbounded serving cache is a kernel OOM-kill, not a JS error.
 *  256 MB is a deliberate floor-ish default for a container of UNKNOWN memory limit: comfortably inside a
 *  512 MB instance (the smallest anyone runs this on) while still holding a useful working set. Tunable. */
export const DEFAULT_MAX_BLOB_CACHE_BYTES = 256 * 1024 * 1024;

export interface BoundedBlobStoreOptions {
  /** Max TOTAL cached bytes across all entries (not an entry count — values are blobs). Integer >= 1. */
  maxBytes?: number;
}

/**
 * The SERVING-side {@link BlobStore}: a byte-bounded LRU over a process-local `Map` (§12 capacity-driven
 * eviction). This is the impl the public read path wants — every read of any published site warms it, so
 * it MUST evict. Bound is on total bytes, since entries are blobs of wildly varying size and a count cap
 * bounds nothing real. A blob larger than the whole cap is simply not stored (not an error): `get` then
 * misses and {@link VerifyingCache} re-fetches + re-verifies it, which is correct — just uncached.
 *
 * Deliberately SEPARATE from {@link MemoryBlobStore}, which is unbounded on purpose for pin/mirror use.
 * Two impls, two roles — do not consolidate them: eviction is required here and a §13 correctness bug there.
 */
export class BoundedBlobStore implements BlobStore {
  readonly #maxBytes: number;
  /** Insertion-ordered; the first key is the least-recently-used (eviction target). Touched via delete-then-set. */
  readonly #entries = new Map<string, Uint8Array>();
  #bytes = 0;

  constructor(opts: BoundedBlobStoreOptions = {}) {
    const maxBytes = opts.maxBytes ?? DEFAULT_MAX_BLOB_CACHE_BYTES;
    if (!Number.isInteger(maxBytes) || maxBytes < 1) {
      throw new RangeError(`blob-cache maxBytes must be an integer >= 1, got ${maxBytes}`);
    }
    this.#maxBytes = maxBytes;
  }

  /** Cached bytes for this CID, or undefined on a miss. A hit COUNTS AS A USE (moves to most-recent). */
  async get(cid: string): Promise<Uint8Array | undefined> {
    const hit = this.#entries.get(cid);
    if (hit === undefined) return undefined;
    this.#entries.delete(cid); // delete-then-set re-inserts at the newest position (LRU refresh)
    this.#entries.set(cid, hit);
    return hit;
  }

  /** Store verified bytes, evicting least-recently-used entries until they fit. A value larger than the
   *  whole cap is dropped rather than stored — it can never fit, and evicting everything for it is worse. */
  async set(cid: string, bytes: Uint8Array): Promise<void> {
    this.#drop(cid); // replacing: retire the old value's bytes before accounting for the new one
    if (bytes.byteLength > this.#maxBytes) return;
    while (this.#bytes + bytes.byteLength > this.#maxBytes) {
      const oldest = this.#entries.keys().next().value;
      if (oldest === undefined) break;
      this.#drop(oldest);
    }
    this.#entries.set(cid, bytes);
    this.#bytes += bytes.byteLength;
  }

  /** Whether this CID is warm. Does NOT count as a use — recency tracks reads, not existence checks. */
  async has(cid: string): Promise<boolean> {
    return this.#entries.has(cid);
  }

  async delete(cid: string): Promise<void> {
    this.#drop(cid);
  }

  #drop(cid: string): void {
    const existing = this.#entries.get(cid);
    if (existing === undefined) return;
    this.#entries.delete(cid);
    this.#bytes -= existing.byteLength;
  }

  /** Current total cached bytes (introspection/tests). */
  get size(): number {
    return this.#bytes;
  }

  /** Current cached entry count (introspection/tests). */
  get count(): number {
    return this.#entries.size;
  }
}

/**
 * The UNBOUNDED {@link BlobStore}: a process-local `Map` that never evicts. For PIN/MIRROR use (§13),
 * where eviction would be a correctness bug — pinned bytes must stay until explicitly unpinned. NOT for
 * the serving cache; that is {@link BoundedBlobStore}'s role. Two impls, two roles — do not consolidate.
 */
export class MemoryBlobStore implements BlobStore {
  readonly #store = new Map<string, Uint8Array>();
  async get(cid: string): Promise<Uint8Array | undefined> {
    return this.#store.get(cid);
  }
  async set(cid: string, bytes: Uint8Array): Promise<void> {
    this.#store.set(cid, bytes);
  }
  async has(cid: string): Promise<boolean> {
    return this.#store.has(cid);
  }
  async delete(cid: string): Promise<void> {
    this.#store.delete(cid);
  }
}

export class VerifyingCache {
  private readonly store: BlobStore;
  private readonly negative: NegativeCache;
  /** In-flight cold fetches, keyed by CID: concurrent getBlob for one CID share ONE fetch + verify. */
  readonly #inflight = new Map<string, Promise<Uint8Array>>();
  /**
   * @param fetcher  transport for cold blob retrieval.
   * @param store    verified-byte backing; defaults to a byte-bounded in-memory {@link BoundedBlobStore},
   *   so an unwired cache is never unbounded. Pass a persistent {@link BlobStore} (e.g. `CacheApiBlobStore`)
   *   to survive restarts — verification is
   *   unchanged either way (every write goes through the re-hash below), so the store carries no trust.
   * @param negative bounded, rate-limited negative cache (§12); defaults to a fresh {@link NegativeCache}. Shared with a `withFetcher` sibling so both instances refuse — and rate-limit — the same unresolvable CIDs.
   */
  constructor(
    private readonly fetcher: Fetcher,
    store: BlobStore = new BoundedBlobStore(),
    negative: NegativeCache = new NegativeCache(),
  ) {
    this.store = store;
    this.negative = negative;
  }

  /**
   * Verified bytes for a signed blob location. Returns the cached copy on a warm hit; otherwise
   * fetches, verifies against `loc.cid`, and caches on match. Throws {@link VerificationError} on a
   * hash mismatch (never cached) and propagates fetch failures.
   *
   * Cold fetches are single-flighted per CID: a burst of concurrent requests for one cold, popular CID
   * collapses into ONE fetch + verify instead of N (§12 thundering-herd guard). A failed fetch is never
   * cached and its rejection propagates to every joiner; the in-flight slot is always cleared on settle,
   * so a rejection cannot poison the key.
   */
  async getBlob(loc: BlobLocation): Promise<Uint8Array> {
    const hit = await this.store.get(loc.cid);
    if (hit !== undefined) return hit;
    const existing = this.#inflight.get(loc.cid);
    if (existing !== undefined) return existing;

    const pending = (async () => {
      const bytes = await this.fetcher.getBlob(loc.did, loc.cid);
      const actual = await recomputeBlobCid(bytes);
      if (actual !== loc.cid) {
        // Fail closed: do NOT cache or serve unverified bytes (§12).
        throw new VerificationError(loc.path, loc.cid, actual);
      }
      await this.store.set(loc.cid, bytes);
      return bytes;
    })();
    this.#inflight.set(loc.cid, pending);
    try {
      return await pending;
    } finally {
      this.#inflight.delete(loc.cid);
    }
  }

  /**
   * Offer bytes claimed to be a signed blob (e.g. a block from a §9.8 transport bundle). Re-hashes
   * them against `cid` via the SAME recompute path as {@link getBlob} and caches ONLY on match,
   * returning whether the block was accepted. A mismatch is refused (returns `false`, nothing cached):
   * a bundle block carries no trust, so a corrupt or forged block simply fails to warm the cache and
   * the later {@link getBlob} fetches + verifies it normally. Never throws on a bad block.
   */
  async offer(cid: string, bytes: Uint8Array): Promise<boolean> {
    if (await this.store.has(cid)) return true;
    const actual = await recomputeBlobCid(bytes);
    if (actual !== cid) return false;
    await this.store.set(cid, bytes);
    return true;
  }

  /**
   * Serve a bare content CID from the signed index of a resolved site (§9.2 RASL-by-CID). The root CID
   * returns the canonical inline-manifest bytes directly; a resource CID goes through verified blob
   * retrieval. An unknown CID (not in THIS site's signed state) is negative-cached — keyed by (site, cid)
   * so a "not in site A" fact cannot mask site B (roadmap H7) — to bound cache-miss amplification (§12,
   * §A.4) and refused. `site` is the resolved index's identity (its root CID); the caller passes the same
   * value it checks {@link isNegativeCached} with.
   */
  async getByCid(site: string, cid: string, location: CidLocation | undefined): Promise<Uint8Array> {
    if (location === undefined) {
      // Unknown FOR THIS SITE: negative-cache (site, cid) (bounded LRU) and charge the global admission
      // bucket. A flood of distinct unknown CIDs drains the bucket → RateLimitError (caller 429s); a
      // per-site repeat short-circuits upstream via isNegativeCached (§12, §A.4 cache-miss amplification).
      if (this.negative.record(site, cid) === "rate-limited") throw new RateLimitError(cid);
      throw new GatewayError(`CID not resolvable from signed state: ${cid}`);
    }
    if (location.kind === "root") return location.bytes;
    return this.getBlob({ did: location.did, cid: location.cid, path: location.path });
  }

  /**
   * A sibling cache that retrieves cold blobs through a DIFFERENT transport while SHARING this cache's
   * verified-byte store AND negative cache. Used to serve one site through a per-site `MirrorFetcher`
   * (§13) over the same persistent, content-addressed store — verification is unchanged (every write still
   * re-hashes via `recomputeBlobCid` before any store.set), so swapping the transport carries no trust.
   */
  withFetcher(fetcher: Fetcher): VerifyingCache {
    return new VerifyingCache(fetcher, this.store, this.negative);
  }

  /** True if this CID was already fetched + verified (warm content cache). */
  has(cid: string): Promise<boolean> {
    return this.store.has(cid);
  }

  /** True if this CID was refused as unknown FOR THIS SITE (negative-cached under (site, cid); H7). */
  isNegativeCached(site: string, cid: string): boolean {
    return this.negative.has(site, cid);
  }
}
